import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { RootState } from "../app/store";
import { useAppDispatch } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import LoadingSpinner from "../components/LoadingSpinner";
import DayTimeSlots from "../components/events/DayTimeSlots";
import BookableSlotWindowsCard from "../components/bookable-slots/BookableSlotWindowsCard";
import {
  groupTimeSlotsByDate,
  sortTimeSlotsByTime,
  filterUpcomingTimeSlots,
} from "../features/utils/timeSlotUtils";
import { fetchAvailableTimeSlots } from "../features/sessions/slice-extensions/sessions-slice-extension";
import { ROUTES } from "../routes";

/** How far ahead the full view looks. */
const VIEW_DAYS = 14;

/**
 * "View All Time Slots": every upcoming free slot in the next two weeks, grouped
 * by day. Slots that are also free at the same time the following weeks are
 * badged "Weekly" and listed first — tapping any slot prefills the new-session form.
 */
const TimeSlotsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { availableTimeSlots, initializationStatus } = useSelector(
    (state: RootState) => state.sessions.timeSlots
  );
  const isLoading =
    initializationStatus === "not-initialized" ||
    initializationStatus === "initializing";

  useEffect(() => {
    dispatch(fetchAvailableTimeSlots({ days: VIEW_DAYS }));
  }, [dispatch]);

  const upcoming = filterUpcomingTimeSlots(availableTimeSlots);
  const grouped = groupTimeSlotsByDate(upcoming);
  const sortedDates = Object.keys(grouped).sort();

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[1280px] 2xl:max-w-[1760px]">
            <div className="space-y-4 py-6">
              <button
                type="button"
                onClick={() => navigate(ROUTES.HOME)}
                className="btn btn-secondary"
              >
                <ChevronLeft size={18} />
                <span>{t("sessions.timeSlotsPage.backToHome")}</span>
              </button>

              <div>
                <h1>{t("sessions.timeSlotsPage.title")}</h1>
                <p className="mt-1 text-grey-30 dark:text-grey-70">
                  {t("sessions.timeSlotsPage.description")}
                </p>
              </div>

              <BookableSlotWindowsCard />

              {isLoading && (
                <div className="flex items-center justify-center p-8">
                  <LoadingSpinner text={t("sessions.loadingTimeSlots")} />
                </div>
              )}

              {!isLoading && sortedDates.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-4 py-8">
                  <img
                    src="/assets/images/icons/available-slots.svg"
                    alt={t("sessions.noAvailableTimeSlots")}
                    className="h-32 w-32 lg:h-40 lg:w-40"
                  />
                  <h3 className="text-lg font-semibold text-grey-20 dark:text-grey-90">
                    {t("sessions.noAvailableTimeSlots")}
                  </h3>
                </div>
              )}

              {!isLoading && sortedDates.length > 0 && (
                <div className="with-border space-y-4 rounded-2xl p-4 lg:space-y-6 lg:p-6">
                  {sortedDates.map((dateKey) => {
                    // Weekly slots first (stable sort keeps time order within each group).
                    const daySlots = sortTimeSlotsByTime(grouped[dateKey]).sort(
                      (a, b) => Number(b.isWeekly) - Number(a.isWeekly)
                    );
                    return (
                      <DayTimeSlots
                        key={dateKey}
                        date={new Date(dateKey)}
                        timeSlots={daySlots}
                        maxSlots={daySlots.length}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimeSlotsPage;
